/**
 * Canonical column catalog for the email-builder `DataTable` component.
 *
 * KEEP IN SYNC with main-consumer/src/lib/data-table-catalog.ts — the
 * renderer in main-consumer projects guest/appointment fields into
 * row records keyed by these exact `key` values; any drift renders as
 * empty cells.
 *
 * `username` and `code` are deliberately excluded — they are login
 * credentials and a spread-everything-from-GuestDoc shortcut would
 * silently leak them to companion siblings.
 */

export type DataTableSource = "companions" | "appointments" | "sessions" | "custom";

/**
 * Sentinel `catalogKey` marking a column inside a data-bound (companions /
 * appointments) table as a CUSTOM-value column: the admin types a header and a
 * static value (per language) that renders the same in every row, instead of
 * binding to a catalog field. Distinct from the `custom` *source* (a fully
 * standalone static table).
 */
export const CUSTOM_COLUMN_KEY = "__custom__";

/**
 * Prefix marking a companion column bound to an event ADDITIONAL (custom) field.
 * The column's `catalogKey` is the fully-qualified `additionalFields.<name>` key
 * — the same convention used by tokens, guest filters and the condition
 * evaluator — so the send-time renderer (consumer + Java PDF) resolves it
 * straight off the companion row. Appointments have no per-guest custom fields,
 * so additional-field columns are offered for the companions source only.
 */
export const ADDITIONAL_FIELD_PREFIX = "additionalFields.";
export const isAdditionalFieldKey = (key: string): boolean =>
  key.startsWith(ADDITIONAL_FIELD_PREFIX);

export type ColumnType = "text" | "image" | "color" | "number" | "currency";

export interface ColumnDef {
  readonly key: string;
  readonly type: ColumnType;
  /** i18n key for the default header label. */
  readonly defaultLabelKey: string;
}

export const COMPANION_COLUMNS: ReadonlyArray<ColumnDef> = [
  { key: "firstName", type: "text", defaultLabelKey: "dataTable.column.firstName" },
  { key: "lastName", type: "text", defaultLabelKey: "dataTable.column.lastName" },
  { key: "email", type: "text", defaultLabelKey: "dataTable.column.email" },
  { key: "phone", type: "text", defaultLabelKey: "dataTable.column.phone" },
  { key: "mobile", type: "text", defaultLabelKey: "dataTable.column.mobile" },
  { key: "company", type: "text", defaultLabelKey: "dataTable.column.company" },
  { key: "jobTitle", type: "text", defaultLabelKey: "dataTable.column.jobTitle" },
  { key: "title", type: "text", defaultLabelKey: "dataTable.column.title" },
  { key: "gender", type: "text", defaultLabelKey: "dataTable.column.gender" },
  { key: "address", type: "text", defaultLabelKey: "dataTable.column.address" },
  { key: "address2", type: "text", defaultLabelKey: "dataTable.column.address2" },
  { key: "zipCode", type: "text", defaultLabelKey: "dataTable.column.zipCode" },
  { key: "city", type: "text", defaultLabelKey: "dataTable.column.city" },
  { key: "country", type: "text", defaultLabelKey: "dataTable.column.country" },
  { key: "language", type: "text", defaultLabelKey: "dataTable.column.language" },
  // Built-in boolean — rendered as a localized Yes/No at send time (the canvas
  // preview shows a sample Yes/No too).
  { key: "emailOptIn", type: "text", defaultLabelKey: "dataTable.column.emailOptIn" },
  // Companion participation — rendered as a localized Accepted/Declined/Pending
  // at send time (the renderer humanizes the raw enum; SYSTEM folds into
  // Accepted). Declined/pending companions are NOT in the guest list, so this
  // column is the place to surface their status.
  { key: "acceptanceState", type: "text", defaultLabelKey: "dataTable.column.acceptanceState" },
  // Per-companion document-portal link. Rendered as a real `/p/{token}` URL at
  // send time (minted per companion by the consumer); the canvas/preview shows
  // a sample URL. Gives the main guest a link to EACH companion's portal.
  { key: "portalLink", type: "text", defaultLabelKey: "dataTable.column.portalLink" },
];

export const APPOINTMENT_COLUMNS: ReadonlyArray<ColumnDef> = [
  { key: "offeringName", type: "text", defaultLabelKey: "dataTable.column.offeringName" },
  { key: "offeringDescription", type: "text", defaultLabelKey: "dataTable.column.offeringDescription" },
  { key: "treatmentName", type: "text", defaultLabelKey: "dataTable.column.treatmentName" },
  { key: "treatmentDuration", type: "text", defaultLabelKey: "dataTable.column.treatmentDuration" },
  { key: "treatmentImage", type: "image", defaultLabelKey: "dataTable.column.treatmentImage" },
  { key: "laneName", type: "text", defaultLabelKey: "dataTable.column.laneName" },
  { key: "laneColor", type: "color", defaultLabelKey: "dataTable.column.laneColor" },
  { key: "appointmentDate", type: "text", defaultLabelKey: "dataTable.column.appointmentDate" },
  { key: "appointmentTime", type: "text", defaultLabelKey: "dataTable.column.appointmentTime" },
  { key: "appointmentEndTime", type: "text", defaultLabelKey: "dataTable.column.appointmentEndTime" },
  { key: "appointmentDuration", type: "text", defaultLabelKey: "dataTable.column.appointmentDuration" },
  { key: "appointmentStatus", type: "text", defaultLabelKey: "dataTable.column.appointmentStatus" },
  { key: "appointmentNotes", type: "text", defaultLabelKey: "dataTable.column.appointmentNotes" },
  { key: "cancelledReason", type: "text", defaultLabelKey: "dataTable.column.cancelledReason" },
];

/**
 * Registered programme sessions of the recipient — the website registration's
 * session-selection step writes `session_registrations`, and the send-time
 * enricher (main-consumer ContextEnricher.enrichSessions) joins them with
 * `event_program_items` into pre-humanized rows (times in the event zone /
 * recipient locale), so every column is plain text.
 */
export const SESSION_COLUMNS: ReadonlyArray<ColumnDef> = [
  { key: "sessionTime", type: "text", defaultLabelKey: "dataTable.column.sessionTime" },
  { key: "sessionEndTime", type: "text", defaultLabelKey: "dataTable.column.sessionEndTime" },
  { key: "sessionTitle", type: "text", defaultLabelKey: "dataTable.column.sessionTitle" },
  { key: "sessionLocation", type: "text", defaultLabelKey: "dataTable.column.sessionLocation" },
];

export function columnsForSource(source: DataTableSource): ReadonlyArray<ColumnDef> {
  if (source === "appointments") return APPOINTMENT_COLUMNS;
  if (source === "sessions") return SESSION_COLUMNS;
  if (source === "custom") return []; // custom tables define their own columns/values inline
  return COMPANION_COLUMNS;
}

export function findColumn(
  source: DataTableSource,
  key: string,
): ColumnDef | null {
  return columnsForSource(source).find((c) => c.key === key) ?? null;
}

/**
 * Column render type for a catalog OR additional-field key. Additional-field
 * columns default to text; an `additionalFields.<name>` key whose field is
 * IMAGE-typed renders as an image column (thumbnail cell — mirrored by the
 * send-time renderers via the marker's `__imageFields` map). Pass the host's
 * AF-name → dataType lookup (e.g. built from useFields) as `afDataTypeByName`;
 * omit it where fields aren't available and AF columns fall back to text.
 */
export function columnTypeForKey(
  source: DataTableSource,
  key: string,
  afDataTypeByName?: ReadonlyMap<string, string>,
): ColumnType {
  const def = findColumn(source, key);
  if (def) return def.type;
  if (isAdditionalFieldKey(key) && afDataTypeByName) {
    // An AF column's render type follows its field dataType: IMAGE → thumbnail,
    // CURRENCY/DOUBLE → right-aligned numeric (value already formatted upstream).
    const dt = afDataTypeByName.get(key.slice(ADDITIONAL_FIELD_PREFIX.length));
    if (dt === "IMAGE") return "image";
    if (dt === "CURRENCY") return "currency";
    if (dt === "DOUBLE") return "number";
  }
  return "text";
}

/** Default columns auto-populated when a new DataTable is dropped into the canvas. */
export const DEFAULT_COLUMNS: Record<DataTableSource, ReadonlyArray<string>> = {
  companions: ["firstName", "lastName", "email"],
  appointments: [
    "offeringName",
    "treatmentName",
    "appointmentDate",
    "appointmentTime",
    "laneName",
  ],
  // All four session columns — the catalog is exactly this wide.
  sessions: ["sessionTime", "sessionEndTime", "sessionTitle", "sessionLocation"],
  // Custom tables don't draw from the catalog — columns/values are typed by
  // the admin and stored on the DataTable node props (customColumns/customRows).
  custom: [],
};
